export interface UserPageVisit {
  userId: string;
  lastPage: string;
  lastPageTitle: string;
  lastVisited: string;
}

export class UserPageTrackingService {
  private static instance: UserPageTrackingService;
  private readonly STORAGE_KEY = 'userPageVisits';

  private constructor() {}

  public static getInstance(): UserPageTrackingService {
    if (!UserPageTrackingService.instance) {
      UserPageTrackingService.instance = new UserPageTrackingService();
    }
    return UserPageTrackingService.instance;
  }

  /**
   * Track a user's page visit
   */
  async trackPageVisit(userId: string, pagePath: string, pageTitle: string): Promise<void> {
    try {
      const visit: UserPageVisit = {
        userId,
        lastPage: pagePath,
        lastPageTitle: pageTitle,
        lastVisited: new Date().toISOString()
      };

      // Store in localStorage for now
      const visits = this.getStoredVisits();
      visits[userId] = visit;
      this.setStoredVisits(visits);
    } catch (error) {
      console.error('Failed to track page visit:', error);
      // Don't throw - this is non-critical functionality
    }
  }

  /**
   * Get the last page a user visited
   */
  async getLastPageVisit(userId: string): Promise<UserPageVisit | null> {
    try {
      const visits = this.getStoredVisits();
      return visits[userId] || null;
    } catch (error) {
      console.error('Failed to get last page visit:', error);
      return null;
    }
  }

  /**
   * Clear a user's page visit history
   */
  async clearPageHistory(userId: string): Promise<void> {
    try {
      const visits = this.getStoredVisits();
      delete visits[userId];
      this.setStoredVisits(visits);
    } catch (error) {
      console.error('Failed to clear page history:', error);
      // Don't throw - this is non-critical functionality
    }
  }

  private getStoredVisits(): Record<string, UserPageVisit> {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  }

  private setStoredVisits(visits: Record<string, UserPageVisit>): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(visits));
    } catch (error) {
      console.error('Failed to store page visits:', error);
    }
  }
}

export const userPageTrackingService = UserPageTrackingService.getInstance();
